// Browser-local email cache backed by IndexedDB (via Dexie).
//
// Privacy principle: email content lives only in the provider mailbox and in
// this browser-local cache — it is never transmitted to or stored on our
// backend. Firestore receives only content-free categorization metadata.
//
// This is a REBUILDABLE CACHE, never a source of truth: the provider mailbox
// holds the content, Firestore holds the categorization. Any wipe (clear site
// data, browser eviction, new device) degrades to "sync again".

import Dexie from 'dexie';

// Multi-account support: the v1 database ('patensys-email') keyed messages by
// provider message id, which is unique per mailbox but not across accounts.
// Dexie cannot change a primary key via an upgrade, so the cache moves to a
// new database with a compound [account+id] primary key. The old database is
// deleted outright — this is a rebuildable cache, the next sync repopulates.
Dexie.delete('patensys-email').catch(() => {});

const db = new Dexie('patensys-email-v2');

db.version(1).stores({
  // [account+id] (PK) = sync account email + provider message id; raw = full
  // provider message object so all existing provider accessors (getBody,
  // getSubjectLine, ...) work unchanged.
  messages: '[account+id], account, threadId, date, fromAddress, [account+threadId], [account+date]',
  meta: 'key',
});

// Bump when matching inputs change (rule sets, horizons, etc.) so cached
// messages are re-processed. Manual processing marks messages with this.
// v2: attachment scanning (stage 0) added — re-scan the cached corpus so
// previously unmatched messages get filename/attachment-content matching.
const PROCESSED_VERSION = 2;

async function saveMessages(entries) {
  // entries: [{account, id, threadId, date, fromAddress, subject, raw}]
  if (!entries || entries.length === 0) {
    return;
  }
  const rows = entries
    .filter((e) => e && e.id && e.account)
    .map((e) => ({ ...e, processedVersion: e.processedVersion ?? null }));
  // bulkPut = upsert; re-synced messages update in place (dedupe by
  // [account+id]). Preserve the existing processedVersion so incremental
  // syncs (which overlap at the boundary by design) don't knock processed
  // messages back into the unprocessed backlog.
  await db.transaction('rw', db.messages, async () => {
    const existing = await db.messages.bulkGet(rows.map((r) => [r.account, r.id]));
    const merged = rows.map((row, i) =>
      existing[i] ? { ...row, processedVersion: existing[i].processedVersion } : row
    );
    await db.messages.bulkPut(merged);
  });
}

async function getThreadMessages(threadId, account) {
  if (!threadId || !account) {
    return [];
  }
  const rows = await db.messages.where('[account+threadId]').equals([account, threadId]).sortBy('date');
  return rows;
}

// Messages not yet processed with the current rule-set version.
async function getUnprocessedMessages(chunkSize = 200, cursor = 0, account = null) {
  if (!account) {
    return [];
  }
  const rows = await db.messages
    .where('[account+date]')
    .between([account, Dexie.minKey], [account, Dexie.maxKey])
    .filter((m) => (m.processedVersion ?? null) !== PROCESSED_VERSION)
    .offset(cursor)
    .limit(chunkSize)
    .toArray();
  return rows;
}

async function countUnprocessedMessages(account) {
  if (!account) {
    return 0;
  }
  return db.messages
    .where('account').equals(account)
    .filter((m) => (m.processedVersion ?? null) !== PROCESSED_VERSION)
    .count();
}

async function markMessagesProcessed(ids, account) {
  if (!ids || ids.length === 0 || !account) {
    return;
  }
  await db.messages
    .where('[account+id]').anyOf(ids.map((id) => [account, id]))
    .modify({ processedVersion: PROCESSED_VERSION });
}

// Partial update of one cached message (e.g. attachmentsParsed/attachmentText).
async function updateMessage(id, fields, account) {
  await db.messages.update([account, id], fields);
}

async function getMeta(key) {
  const row = await db.meta.get(key);
  return row ? row.value : null;
}

async function setMeta(key, value) {
  await db.meta.put({ key, value });
}

async function countMessages(account) {
  if (!account) {
    return db.messages.count();
  }
  return db.messages.where('account').equals(account).count();
}

// All cached messages of one account, oldest first (used to build the local
// search index).
async function getAllMessages(account) {
  if (!account) {
    return [];
  }
  return db.messages.where('[account+date]')
    .between([account, Dexie.minKey], [account, Dexie.maxKey])
    .toArray();
}

async function clearCache() {
  await db.messages.clear();
  await db.meta.clear();
}

// Ask the browser to exclude this origin's data from storage-pressure
// eviction. Best-effort; resolves true/false.
async function requestPersistence() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist();
    }
  } catch (e) {
    console.warn('storage.persist() failed', e);
  }
  return false;
}

export {
  PROCESSED_VERSION,
  saveMessages,
  getThreadMessages,
  getUnprocessedMessages,
  countUnprocessedMessages,
  markMessagesProcessed,
  updateMessage,
  getMeta,
  setMeta,
  countMessages,
  getAllMessages,
  clearCache,
  requestPersistence,
};
